import { format, parseISO, getDay } from 'date-fns';
import { getDayOfWeek } from '../../../../helpers/utils/utils';
import { tipoExibicaoHistorico } from '../../../../models/types';

/**
 * Mesma ordenação fixa da tela real (`historicoProcessoController.
 * ORDENACAO_HISTORICOS_PROCESSO`) — itens fixados (`fixoAcomp`) aparecem
 * primeiro, sem controle de ordenação pelo usuário nesta feature.
 */
export const ORDENACAO_HISTORICOS_PROCESSO = ['fixoAcomp,ASC', 'idProcessoAcompanhamento,ASC'];

/**
 * Prefixos que a tela real destaca em negrito dentro do texto do histórico
 * (`formatacaoAcordoHistorico`, `HistoricoProcesso` real). Porte SEM usar
 * HTML cru: a tela real usa `html-react-parser` sobre `historico.texto`
 * (dado do backend) concatenado com tags — funcionalmente equivalente a
 * `dangerouslySetInnerHTML`, um vetor de XSS caso o backend algum dia
 * devolva texto malicioso (armazenado por outro usuário/integração). Este
 * porte NUNCA interpreta `texto` como HTML — só localiza o prefixo conhecido
 * e retorna partes separadas para o componente renderizar como texto puro
 * (um `<strong>` real do React, nunca `parse()`/`dangerouslySetInnerHTML`).
 */
const PREFIXOS_DESTAQUE = ['Simulação:', 'Acordo:', 'Simulação nº ', 'Simulação aguardando aprovação de'];

export function formatarPartesTexto(texto) {
  if (!texto) {
    return [];
  }
  const prefixo = PREFIXOS_DESTAQUE.find(p => texto.startsWith(p));
  if (!prefixo) {
    return [{ negrito: false, texto }];
  }
  return [
    { negrito: true, texto: prefixo },
    { negrito: false, texto: texto.slice(prefixo.length) },
  ];
}

function formatarDataHora(dataCadastramento) {
  try {
    return format(parseISO(dataCadastramento), 'dd/MM/yyyy - HH:mm');
  } catch {
    return '';
  }
}

function formatarDataLabel(dataCadastramento) {
  try {
    const data = parseISO(dataCadastramento);
    return `${format(data, 'dd/MM/yyyy')} - ${getDayOfWeek(getDay(data))}`;
  } catch {
    return '';
  }
}

/**
 * Cor por `idTipoHistorico` — mesma tabela `types` de
 * `HistoricoProcesso.styles.jsx` real (só as cores; os ícones de pin por
 * tipo daquele arquivo são o MESMO glifo genérico recolorido por tipo, não
 * ícones distintos — reaproveitados aqui como um único `PushPinIcon`
 * colorido dinamicamente em `ListaHistorico.jsx`, em vez de embutir os 10
 * SVGs quase idênticos). Índice 1-10 é o `idTipoHistorico` real do backend.
 */
const CORES_TIPO_HISTORICO = {
  1: '#707070',
  2: '#CB0000',
  3: '#03A500',
  4: '#EAB600',
  5: '#FF5C00',
  6: '#7F2EBE',
  7: '#000571',
  8: '#034DDC',
  9: '#1B9D8F',
  10: '#034DDC',
};
const COR_TIPO_HISTORICO_PADRAO = '#707070';

export function corTipoHistorico(idTipoHistorico) {
  return CORES_TIPO_HISTORICO[idTipoHistorico] || COR_TIPO_HISTORICO_PADRAO;
}

function mapearHistorico(historico) {
  return {
    idProcessoHistorico: historico.idProcessoHistorico,
    idTipoHistorico: historico.idTipoHistorico,
    descricaoTipo: historico.descricaoTipo || '',
    dataHora: formatarDataHora(historico.dataCadastramento),
    usuario: [historico.apelido, historico.nomeUsuario, historico.email].filter(Boolean).join(' - '),
    partesTexto: formatarPartesTexto(historico.texto),
    faseAtualProcesso: historico.faseAtualProcesso || '',
    faseSeguinteProcesso: historico.faseSeguinteProcesso || '',
    fixar: historico.fixar || tipoExibicaoHistorico.naoFixo,
  };
}

/**
 * Agrupa por data em runs consecutivos (não por um mapa `data -> itens`
 * indexado globalmente) — versão simplificada do `dateIndicator` da tela
 * real (`historicoProcessoController.makeDateIndicator`/`makeItems`), que
 * usava um contador `unique` + chave composta `` `${unique}?${data}` `` só
 * para evitar colisão de chave num objeto plano quando a MESMA data
 * reaparece de forma não-contígua (ex.: item fixado do dia X intercalado
 * entre itens do dia Y). Retornar um ARRAY de grupos em vez de um objeto
 * elimina a necessidade da chave composta por completo — dois grupos não-
 * contíguos com a mesma `dataLabel` continuam sendo entradas SEPARADAS do
 * array (nunca se fundem), sem precisar de um contador auxiliar.
 */
export function agruparHistoricosPorData(content = []) {
  const grupos = [];

  content.forEach((historicoBruto) => {
    if (!historicoBruto.dataCadastramento) {
      return;
    }

    const historico = mapearHistorico(historicoBruto);
    const dataLabel = formatarDataLabel(historicoBruto.dataCadastramento);
    const grupoAtual = grupos[grupos.length - 1];

    if (!grupoAtual || grupoAtual.dataLabel !== dataLabel) {
      grupos.push({ dataLabel, itens: [historico] });
    } else {
      grupoAtual.itens.push(historico);
    }
  });

  return grupos;
}
